import { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, MessageCircle } from 'lucide-react';
import Button from '../base/Button';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    travelDates: '',
    groupSize: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Préparer les données pour l'envoi
      const emailBody = `
Nouveau message de contact depuis le site web

Nom: ${formData.name}
Email: ${formData.email}
Téléphone: ${formData.phone || 'Non renseigné'}
Sujet: ${formData.subject}
Nombre de personnes: ${formData.groupSize || 'Non renseigné'}
Dates de voyage: ${formData.travelDates || 'Non renseigné'}

Message:
${formData.message}
      `.trim();

      // Envoi via mailto (solution temporaire - à remplacer par un endpoint backend)
      const mailtoLink = `mailto:contact@mvd.perle-in.com?subject=${encodeURIComponent(`Contact: ${formData.subject}`)}&body=${encodeURIComponent(emailBody)}`;
      
      // Pour un envoi réel, vous devrez configurer un endpoint backend
      // Exemple: const response = await fetch('/api/contact', { method: 'POST', body: JSON.stringify(formData) });
      
      // Simulation d'envoi réussi (à remplacer par un vrai endpoint)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        travelDates: '',
        groupSize: ''
      });
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      alert('Une erreur est survenue. Veuillez nous contacter directement par email ou WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Téléphone & WhatsApp",
      content: "+971 50 62 11 791",
      link: "https://web.whatsapp.com/send?phone=971506211791",
      description: "Disponible 7j/7 de 9h à 21h (heure de Dubaï)"
    },
    {
      icon: Mail,
      title: "Email",
      content: "contact@mvd.perle-in.com",
      link: "mailto:contact@mvd.perle-in.com",
      description: "Réponse garantie sous 24h"
    },
    {
      icon: MapPin,
      title: "Adresse",
      content: "Dubai Silicon Oasis, Dubai, UAE",
      link: "https://maps.google.com/?q=Dubai+Silicon+Oasis",
      description: "Équipe française basée à Dubaï"
    },
    {
      icon: Clock,
      title: "Horaires",
      content: "7j/7 - 9h à 21h",
      description: "Support client en français"
    }
  ];

  if (isSubmitted) {
    return (
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white" id="contact">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-xl">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Message envoyé avec succès !
            </h2>
            <p className="text-gray-600 mb-6">
              Notre équipe vous répondra dans les plus brefs délais. 
              En attendant, n'hésitez pas à nous contacter sur WhatsApp pour une réponse immédiate.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => setIsSubmitted(false)}
                variant="outline"
              >
                Envoyer un autre message
              </Button>
              <a
                href="https://web.whatsapp.com/send?phone=971506211791"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="w-full sm:w-auto">
                  <MessageCircle size={20} className="mr-2" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white" id="contact">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Contactez Notre <span className="text-amber-600">Équipe</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Notre équipe française basée à Dubaï est là pour vous accompagner. 
            Posez-nous vos questions, demandez un devis personnalisé ou réservez directement.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-8">
              Plusieurs façons de nous joindre
            </h3>
            
            <div className="space-y-6 mb-8">
              {contactInfo.map((info, index) => (
                <div key={index} className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <info.icon size={24} className="text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">{info.title}</h4>
                    {info.link ? (
                      <a
                        href={info.link}
                        target={info.link.startsWith('http') ? '_blank' : undefined}
                        rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="text-amber-600 hover:text-amber-700 font-medium transition-colors"
                      >
                        {info.content}
                      </a>
                    ) : (
                      <p className="text-gray-900 font-medium">{info.content}</p>
                    )}
                    <p className="text-gray-600 text-sm mt-1">{info.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick actions */}
            <div className="bg-blue-900 rounded-xl p-6 text-white">
              <h4 className="font-semibold mb-4">Besoin d'une réponse immédiate ?</h4>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://web.whatsapp.com/send?phone=971506211791"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button variant="secondary" className="w-full">
                    <MessageCircle size={20} className="mr-2" />
                    WhatsApp
                  </Button>
                </a>
                <a
                  href="tel:+971506211791"
                  className="flex-1"
                >
                  <Button variant="outline" className="w-full border-white text-white hover:bg-white/20 hover:text-white">
                    <Phone size={20} className="mr-2" />
                    Appeler
                  </Button>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8 shadow-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Envoyez-nous un message
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors"
                    placeholder="Votre nom"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors"
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>
                
                <div>
                  <label htmlFor="groupSize" className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre de personnes
                  </label>
                  <select
                    id="groupSize"
                    name="groupSize"
                    value={formData.groupSize}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors"
                  >
                    <option value="">Sélectionner</option>
                    <option value="1">1 personne</option>
                    <option value="2">2 personnes</option>
                    <option value="3-4">3-4 personnes</option>
                    <option value="5-8">5-8 personnes</option>
                    <option value="9+">9+ personnes</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="travelDates" className="block text-sm font-medium text-gray-700 mb-2">
                    Dates de voyage
                  </label>
                  <input
                    type="text"
                    id="travelDates"
                    name="travelDates"
                    value={formData.travelDates}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors"
                    placeholder="Ex: 15-22 mars 2025"
                  />
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Sujet *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors"
                  >
                    <option value="">Choisir un sujet</option>
                    <option value="devis">Demande de devis</option>
                    <option value="reservation">Réservation</option>
                    <option value="information">Information activité</option>
                    <option value="modification">Modification/Annulation</option>
                    <option value="reclamation">Réclamation</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-colors resize-none"
                  placeholder="Décrivez votre demande, vos préférences, votre budget..."
                />
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.message.length}/500 caractères
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                loading={isSubmitting}
                disabled={!formData.name || !formData.email || !formData.subject || !formData.message || isSubmitting}
              >
                {isSubmitting ? (
                  'Envoi en cours...'
                ) : (
                  <>
                    <Send size={20} className="mr-2" />
                    Envoyer le message
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                En envoyant ce formulaire, vous acceptez d'être contacté par notre équipe 
                concernant votre demande. Vos données sont protégées et ne seront jamais partagées.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}